import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import LoadingImage from 'components/LoadingImage';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAppSelector } from 'store/hooks';
import API, { APIGetLabBySlug } from 'utils/api';

const emailTemplates = {
  en: {
    subject: 'Technical Lab Invitation – ',
    salutation: 'Hi,',
    messageHeader: 'As part of our recruitment process, we\'d like you to complete a short technical lab to better understand your skills and way of working.',
    messageFooter: 'The test is interactive and runs directly in your browser – no setup or installation needed.\n\nIf you have any questions or issues, feel free to reply to this email.\n\nBest of luck,'
  },
  fr: {
    subject: 'Invitation au Lab Technique – ',
    salutation: 'Bonjour,',
    messageHeader: 'Dans le cadre de notre processus de recrutement, nous aimerions que vous complétiez un court lab technique pour mieux comprendre vos compétences et votre façon de travailler.',
    messageFooter: 'Le test est interactif et s\'exécute directement dans votre navigateur – aucune installation n\'est nécessaire.\n\nSi vous avez des questions ou des problèmes, n\'hésitez pas à répondre à cet e-mail.\n\nBonne chance,'
  }
};

const OrgInviteCandidatePage = () => {
  const navigate = useNavigate();
  const { lab_slug } = useParams<{ lab_slug: string }>();
  const user = useAppSelector((state) => state.auth.user_me);
  const selectedOrgId = useAppSelector((state) => state.auth.selectedOrganizationId);
  const [orgName, setOrgName] = useState("");

  const [isInviteSent, setIsInviteSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [currentLang, setCurrentLang] = useState(user?.lang || 'en');

  const lab = useQuery({
    queryKey: ['lab', lab_slug],
    queryFn: () => APIGetLabBySlug(lab_slug),
    select: (data) => data.data.length > 0 ? data.data[0] : null,
  });

  useEffect(() => {
    if (selectedOrgId) {
      const org = user?._organizations.find((org) => org.id === selectedOrgId);
      setOrgName(org?.name || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const recruiterName = user?.firstname + " " + user?.lastname;

  const getInitialValues = (lang: string) => ({
    user_email: '',
    user_firstname: '',
    user_lastname: '',
    lang: lang,
    optional_message: '',
    subject: `${emailTemplates[lang].subject}${lab.data?.name}`,
    salutation: emailTemplates[lang].salutation,
    messageHeader: emailTemplates[lang].messageHeader,
    messageFooter: `${emailTemplates[lang].messageFooter}\n${recruiterName}\n${orgName}`,
  });

  const [initialValues, setInitialValues] = useState(getInitialValues(currentLang));

  useEffect(() => {
    setInitialValues(getInitialValues(currentLang));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLang, lab.data?.name]);

  const validationSchema = Yup.object().shape({
    user_email: Yup.string().email('Invalid email address').required('Candidate email is required'),
    user_firstname: Yup.string().required('Candidate first name is required'),
    user_lastname: Yup.string().required('Candidate last name is required'),
    lang: Yup.string().required('Language is required'),
    optional_message: Yup.string(),
    subject: Yup.string().required('Subject is required'),
    salutation: Yup.string().required('Salutation is required'),
    messageHeader: Yup.string().required('Message header is required'),
    messageFooter: Yup.string().required('Message footer is required'),
  });

  const sendInvitationMutation = useMutation({
    mutationFn: (invitationData: any) => {
      return API.post('/assessment-invitations', invitationData);
    },
    onSuccess: (data, variables) => {
      console.log("Invitation sent successfully:", data);
      setSentEmail(variables.user_email);
      setIsInviteSent(true);
    },
    onError: (error: any) => {
      console.error("Error sending invite:", error);
      setSubmitError(error?.response?.data?.message || error?.message || "Failed to send invitation. Please try again.");
    },
  });

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setSubmitError(null);
    let org = user?._organizations.find((org) => org.id === selectedOrgId);
    if (!org) {
      org = user?._organizations[0];
    }
    const payload = {
      lab_slug: lab_slug,
      organisation_id: org?.id,
      organisation_email: org?.email,
      user_email: values.user_email,
      user_firstname: values.user_firstname,
      user_lastname: values.user_lastname,
      lang: values.lang,
      optional_message: values.optional_message,
      email_template_vars: {
        subject: values.subject,
        salutation: values.salutation,
        message_header: values.messageHeader,
        message_footer: values.messageFooter,
      }
    };

    sendInvitationMutation.mutate(payload, {
      onSettled: () => {
        setSubmitting(false);
      },
      onSuccess: () => {
        resetForm();
      }
    });
  };

  const handleSendAnother = () => {
    setIsInviteSent(false);
    navigate('/recruiter/qualification-catalog');
    setSentEmail('');
  };
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    setCurrentLang(newLang);
  };


  const handleViewActivity = () => {
    navigate('/recruiter/activity');
  };

  return (
    <section className="flex w-full h-full border-l-[1px] border-[#F5F5F5]">
      <div className="flex flex-col pt-[56px] px-[46px] h-full w-full">
        {/* TODO: Consider if this header is still needed or comes from a parent layout */}
        <div className="mb-10 text-[#111827]">
          <h1 className="custom-text-[volkhov-700-35px]">
            Hello {orgName}
          </h1>
          <p className="custom-text-[poppins-400-18px]">
            Here is an overview or current status.
          </p>
        </div>

        <div className="bg-white rounded-[20px] shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)] p-8 max-w-3xl mx-auto w-full mb-10">
          {isInviteSent ? (
            <div className="flex flex-col items-center py-8 text-center">
              <img
                className="w-[209px] h-[183px] mb-8"
                alt="Celebration"
                src={require("assets/test-invitation-celebration.gif")}
              />
              <h2 className="custom-text-[poppins-600-32px] text-[#141414] mb-[9px]">
                Invite Sent Successfully!
              </h2>
              <p className="custom-text-[poppins-400-16px] text-[#6B7280] mb-[39px] max-w-md">
                We’ve sent the lab to <span className="font-medium">{sentEmail}</span>.
                <br />
                You’ll be notified as soon as the candidate completes it.
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleSendAnother}
                  className="rounded-[10px] border border-[#407EC9] px-[21px] py-[11px] text-[#407EC9] transition-colors custom-text-[poppins-500-15px] hover:bg-blue-50"
                >
                  Send another Invite
                </button>
                <button
                  type="button"
                  onClick={handleViewActivity}
                  className="rounded-[11px] bg-[#407EC9] px-[47px] py-[10px] text-white transition-colors custom-text-[poppins-500-15px] hover:bg-[#3569A7]"
                >
                  View activity
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* TODO: Update title based on selected lab */}
              <h2 className="custom-text-[poppins-600-32px] text-[#111827] mb-[40px] text-left">
                Invite Candidate to Take This Lab
              </h2>

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true} // Keep true if labData might change
              >
                {({ isSubmitting, errors, touched }) => (
                  <Form> {/* Remove space-y from Form */}
                    <div className="flex flex-row flex-wrap gap-4 w-full md:flex-nowrap">
                      {/* Candidate First Name */}
                      <div className="mb-[22px] w-full">
                        <label
                          htmlFor="user_firstname"
                          className="block mb-[13px] custom-text-[poppins-500-16px] text-[#1f2937]"
                        >
                          Candidate First Name
                        </label>
                        <Field
                          id="user_firstname"
                          name="user_firstname"
                          type="text"
                          className={`w-full rounded-[10px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)] border border-gray-200 outline-none bg-white custom-text-[poppins-400-14px] ${errors.user_firstname && touched.user_firstname ? 'ring-2 ring-red-500' : ''}`}
                        />
                        <ErrorMessage name="user_firstname" component="p" className="mt-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />
                      </div>

                      {/* Candidate Last Name */}
                      <div className="mb-[22px] w-full">
                        <label
                          htmlFor="user_lastname"
                          className="block mb-[13px] custom-text-[poppins-500-16px] text-[#1f2937]"
                        >
                          Candidate Last Name
                        </label>
                        <Field
                          id="user_lastname"
                          name="user_lastname"
                          type="text"
                          className={`w-full rounded-[10px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)] border border-gray-200 outline-none bg-white custom-text-[poppins-400-14px] ${errors.user_lastname && touched.user_lastname ? 'ring-2 ring-red-500' : ''}`}
                        />
                        <ErrorMessage name="user_lastname" component="p" className="mt-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />
                      </div>
                    </div>

                    <div className="flex flex-row flex-wrap gap-4 w-full md:flex-nowrap">
                      {/* Candidate Email Address */}
                      <div className="mb-[22px] w-full md:w-2/3"> {/* Add margin bottom */}
                        <label
                          htmlFor="user_email"
                          className="block mb-[13px] custom-text-[poppins-500-16px] text-[#1f2937]"
                        >
                          Candidate Email address
                        </label>
                        <Field
                          id="user_email"
                          name="user_email"
                          type="email"
                          className={`w-full rounded-[10px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)] border border-gray-200 outline-none bg-white custom-text-[poppins-400-14px] ${errors.user_email && touched.user_email ? 'ring-2 ring-red-500' : ''}`}
                        />
                        <ErrorMessage name="user_email" component="p" className="mt-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />
                      </div>
                      <div className="mb-[22px] w-full md:w-1/3">
                        <label
                          htmlFor="lang"
                          className="block mb-[13px] custom-text-[poppins-500-16px] text-[#1f2937]"
                        >
                          Language
                        </label>
                        <Field
                          as="select"
                          id="lang"
                          name="lang"
                          onChange={(e) => {
                            handleLanguageChange(e);
                            const { onChange } = e.target;
                            onChange(e);
                          }}
                          className={`w-full rounded-[10px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)] border border-gray-200 outline-none bg-white custom-text-[poppins-400-14px] appearance-none ${errors.lang && touched.lang ? 'ring-2 ring-red-500' : ''}`}
                        >
                          <option value="en">English</option>
                          <option value="fr">French</option>
                        </Field>
                        <ErrorMessage name="lang" component="p" className="mt-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />
                      </div>
                    </div>
                    {/* Subject */}
                    <div className="mb-[22px]"> {/* Add margin bottom */}
                      <label
                        htmlFor="subject"
                        className="block mb-[13px] custom-text-[poppins-500-16px] text-[#1f2937]"
                      >
                        Subject
                      </label>
                      <Field
                        id="subject"
                        name="subject"
                        type="text"
                        className={`w-full rounded-[10px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)] border border-gray-200 outline-none bg-white custom-text-[poppins-400-14px] ${errors.subject && touched.subject ? 'ring-2 ring-red-500' : ''}`}
                      />
                      <ErrorMessage name="subject" component="p" className="mt-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />
                    </div>


                    {/* Email Template Parts */}
                    <div className="mb-[22px]"> {/* Add margin bottom */}
                      <label className="block mb-[13px] custom-text-[poppins-500-16px] text-[#1f2937]">
                        Email Message Template
                      </label>
                      {/* Combined Message Block */}
                      <div className="rounded-[10px] border border-gray-200 overflow-hidden shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)]">
                        {/* Salutation */}
                        <Field
                          id="salutation"
                          name="salutation"
                          as="textarea"
                          rows="1"
                          placeholder="Hi,"
                          className={`w-full px-4 py-3 focus:ring-0 focus:outline-none bg-white custom-text-[poppins-400-16px] leading-relaxed resize-none border-none ${errors.salutation && touched.salutation ? 'ring-2 ring-inset ring-red-500' : ''}`}
                        />
                        <ErrorMessage name="salutation" component="p" className="px-4 pb-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />

                        {/* Header */}
                        <Field
                          id="messageHeader"
                          name="messageHeader"
                          as="textarea"
                          rows="3" // Adjust rows
                          placeholder="As part of our recruitment process..."
                          className={`w-full px-4 py-3 focus:ring-0 focus:outline-none bg-white custom-text-[poppins-400-16px] leading-relaxed resize-none border-t border-gray-200 ${errors.messageHeader && touched.messageHeader ? 'ring-2 ring-inset ring-red-500' : ''}`}
                        />
                        <ErrorMessage name="messageHeader" component="p" className="px-4 pb-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />

                        {/* Non-Editable Lab Details Block - TODO: Get labDetailsBlockString from state/props if lab is dynamic */}
                        <div className="bg-gray-50 border-t border-gray-200 px-4 py-4 custom-text-[poppins-400-16px] leading-relaxed whitespace-pre-wrap text-gray-700">
                          {`
✓ Lab: [${lab.data?.name}]
⏱ Duration: ~${lab.data?.duration} minutes
`}
                        </div>

                        {/* Footer */}
                        <Field
                          id="messageFooter"
                          name="messageFooter"
                          as="textarea"
                          rows="8" // Adjust rows
                          placeholder="The test is interactive..."
                          className={`w-full px-4 py-3 focus:ring-0 focus:outline-none bg-white custom-text-[poppins-400-16px] leading-relaxed resize-none border-none border-t border-gray-200 ${errors.messageFooter && touched.messageFooter ? 'ring-2 ring-inset ring-red-500' : ''}`}
                        />
                        <ErrorMessage name="messageFooter" component="p" className="px-4 pb-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />
                      </div>
                    </div>

                    {/* Optional Message */}
                    {/* <div className="mb-[22px]">
                      <label
                        htmlFor="optional_message"
                        className="block mb-[13px] custom-text-[poppins-500-16px] text-[#1f2937]"
                      >
                        Optional Internal Message (Visible to candidate if feature enabled)
                      </label>
                      <Field
                        id="optional_message"
                        name="optional_message"
                        as="textarea"
                        rows="3"
                        placeholder="Add any internal notes or specific instructions here..."
                        className={`w-full rounded-[10px] px-4 py-3 focus:ring-2 focus:ring-[#407EC9] shadow-[0px_4px_10px_0px_rgba(0,0,0,0.05)] border border-gray-200 outline-none bg-white custom-text-[poppins-400-14px] ${errors.optional_message && touched.optional_message ? 'ring-2 ring-red-500' : ''}`}
                      />
                      <ErrorMessage name="optional_message" component="p" className="mt-1 text-sm text-red-500 custom-text-[poppins-400-12px]" />
                    </div> */}

                    {/* Submit Button */}
                    <div className="flex justify-end pt-4">
                      <button
                        type="submit"
                        className="rounded-lg bg-[#407EC9] px-6 py-3 text-white transition-colors custom-text-[poppins-500-15px] hover:bg-[#3569A7] disabled:opacity-50"
                        disabled={sendInvitationMutation.isPending}
                      >
                        {sendInvitationMutation.isPending ? <LoadingImage className="!h-6 !w-auto" /> : 'Send invitation'}
                      </button>
                    </div>
                    {submitError && (
                      <div className="text-red-500 custom-text-[poppins-400-14px] text-right p-2 rounded-md">
                        {submitError}
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default OrgInviteCandidatePage;